/**
 * Centralized token error handling utilities
 * Provides consistent error handling patterns for token refresh failures
 * Integrates with fallback mechanisms and user notifications
 */

import { getValidToken } from '../redux/slices/userSlice';
import { executeFallback, OperationType } from './fallbackManager';
import { AuthNotifications } from './notificationManager';
import { AppDispatch } from '../redux/store';

export interface TokenErrorHandlerOptions {
  operation: string;
  operationType?: OperationType;
  showUserNotification?: boolean;
  logError?: boolean;
  enableFallback?: boolean;
  dispatch?: AppDispatch;
  fallbackAction?: () => void;
  metadata?: Record<string, any>;
}

/**
 * Enhanced token error handler with fallback mechanisms and notifications
 */
export const handleTokenError = async (
  error: any, 
  options: TokenErrorHandlerOptions
): Promise<string> => {
  const { 
    operation, 
    operationType, 
    logError = true, 
    enableFallback = false,
    showUserNotification = false,
    dispatch,
    fallbackAction,
    metadata 
  } = options;
  
  // Create user-friendly error message
  let userMessage = 'Authentication failed. Please sign in again.';
  
  // Log detailed error for debugging if enabled
  if (logError) {
    console.error(`[TokenErrorHandler] Token refresh failed for ${operation}:`, error);
  }
  
  // Handle specific error types with improved messaging
  if (error?.message) {
    const message = error.message.toLowerCase();
    if (message.includes('network') || message.includes('timeout')) {
      userMessage = 'Network error. Please check your connection and try again.';
      if (showUserNotification) {
        AuthNotifications.networkError();
      }
    } else if (message.includes('expired') || message.includes('token')) {
      userMessage = 'Your session has expired. Please sign in again.';
      if (showUserNotification) {
        AuthNotifications.sessionExpired();
      }
    } else if (message.includes('invalid') || message.includes('unauthorized')) {
      userMessage = 'Authentication error. Please sign in again.';
      if (showUserNotification) {
        AuthNotifications.authError(userMessage);
      }
    } else if (message.includes('service') || message.includes('unavailable')) {
      userMessage = 'Service temporarily unavailable. Please try again later.';
      if (showUserNotification) {
        AuthNotifications.serviceUnavailable();
      }
    }
  }
  
  // Execute fallback strategies if enabled
  if (enableFallback && operationType && dispatch) {
    try {
      const fallbackResult = await executeFallback(
        error,
        operation,
        operationType,
        dispatch,
        metadata
      );
      
      if (fallbackResult.success) {
        // Fallback succeeded, update user message
        userMessage = fallbackResult.userMessage || userMessage;
        
        if (showUserNotification && fallbackResult.userMessage) {
          const notificationType = fallbackResult.action.includes('degrade') ? 'warning' : 'info';
          AuthNotifications[notificationType === 'warning' ? 'readOnlyMode' : 'tokenRefreshing']();
        }
      } else {
        // Fallback failed, ensure user is notified
        if (showUserNotification) {
          AuthNotifications.authError(fallbackResult.userMessage || userMessage);
        }
      }
    } catch (fallbackError) {
      console.error(`[TokenErrorHandler] Fallback execution failed for ${operation}:`, fallbackError);
      if (showUserNotification) {
        AuthNotifications.authError('Unable to recover from authentication error');
      }
    }
  }
  
  // Execute custom fallback action if provided (legacy support)
  if (fallbackAction) {
    try {
      fallbackAction();
    } catch (fallbackError) {
      console.error(`[TokenErrorHandler] Custom fallback action failed for ${operation}:`, fallbackError);
    }
  }
  
  return userMessage;
};

/**
 * Wraps getValidToken calls with consistent error handling
 */
export const getValidTokenWithErrorHandling = async (
  dispatch: any,
  operation: string,
  options?: Partial<TokenErrorHandlerOptions>
): Promise<{ token: string | null; error: string | null }> => {
  try {
    const token = await dispatch(getValidToken()).unwrap();
    return { token, error: null };
  } catch (error) {
    const errorMessage = await handleTokenError(error, {
      operation,
      logError: true,
      ...options
    });
    return { token: null, error: errorMessage };
  }
};

/**
 * Common error messages for different operations
 */
export const TOKEN_ERROR_MESSAGES = {
  ADMIN_API: 'Unable to access admin features. Please sign in again.',
  CHAT_OPERATIONS: 'Unable to perform chat operation. Please sign in again.',
  MESSAGE_OPERATIONS: 'Unable to process message. Please sign in again.',
  CONVERSATION_OPERATIONS: 'Unable to access conversations. Please sign in again.',
  PAGINATION: 'Unable to load more content. Please sign in again.',
} as const;

/**
 * Determines if an error is token-related
 */
export const isTokenError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error?.message?.toLowerCase() || '';
  const tokenErrorKeywords = [
    'token',
    'authentication',
    'unauthorized',
    'expired',
    'invalid',
    'sign in',
    'login'
  ];
  
  return tokenErrorKeywords.some(keyword => errorMessage.includes(keyword));
};
